import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, XCircle, AlertTriangle, FileText, RefreshCw, Mail } from 'lucide-react';

const RefundPolicyPage = () => (
  <div style={{ minHeight: '100vh', background: '#f8fafc', color: '#1e293b' }}>
    <div style={{ background: '#0f172a', color: 'white', padding: '40px 24px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <Link to="/" data-testid="refund-policy-back-link" style={{ display: 'inline-flex', gap: 8, color: '#94a3b8', textDecoration: 'none', alignItems: 'center', marginBottom: 18 }}>
          <ArrowLeft size={18} /> Back to Home
        </Link>
        <h1 data-testid="refund-policy-page-title" style={{ fontSize: '2.4rem', margin: 0, color: 'white' }}>MedCare Refund Policy</h1>
        <p style={{ color: '#cbd5e1', maxWidth: 720, marginTop: 12, lineHeight: 1.6 }}>
          The following refund policy applies to every paid product offered by
          MedCare Nursing Institute &amp; Training Academy, including course
          enrollment fees, mid-term and final exam fees, association membership
          subscriptions, and ancillary services.
        </p>
      </div>
    </div>

    <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 24px 80px' }}>
      {/* Headline rule */}
      <section data-testid="refund-headline" style={{ background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: 14, padding: 26, marginBottom: 22 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <XCircle size={28} color="#b91c1c" style={{ flexShrink: 0 }} />
          <div>
            <h2 style={{ margin: 0, color: '#7f1d1d', fontSize: '1.35rem' }}>ALL SALES ARE FINAL</h2>
            <p style={{ marginTop: 10, color: '#7f1d1d', lineHeight: 1.65 }}>
              Due to the digital nature of MedCare&apos;s online courses, study
              materials, downloadable training manuals, certificates, and association
              membership benefits, <strong>all sales are final and non-refundable</strong>.
              By enrolling or subscribing, you acknowledge and agree to this no-refund
              policy.
            </p>
          </div>
        </div>
      </section>

      {[
        {
          id: 'course-enrollment',
          icon: FileText,
          title: '1. Course Enrollment Fees ($99)',
          body: (
            <>
              <p>
                The $99 program-enrollment fee unlocks immediate access to course
                modules, downloadable study materials, the AI tutor, and the
                quiz/exam system. <strong>This fee is non-refundable</strong> once
                payment has been processed, even if the student has not yet
                accessed any module.
              </p>
              <p>
                If the student does not complete the course, the enrollment fee is
                still consumed and may not be transferred to another student,
                another course, or another time period.
              </p>
            </>
          ),
        },
        {
          id: 'exam-fees',
          icon: FileText,
          title: '2. Mid-Term and Final Exam Fees ($99 / $199)',
          body: (
            <>
              <p>
                Mid-term and final exam fees ($99 each for standard programs; $199
                for doctorate finals) are <strong>non-refundable</strong> once
                payment has been processed. This applies whether or not the student
                attempts, passes, or fails the exam.
              </p>
              <p>
                Students who fail an exam may pay again to retake it. Each retake is
                a new, separate, non-refundable transaction.
              </p>
            </>
          ),
        },
        {
          id: 'membership',
          icon: RefreshCw,
          title: '3. Association Membership Subscription ($9.99 / month)',
          body: (
            <>
              <p>
                The MedCare Association membership ($9.99 / month) is a
                <strong> recurring subscription</strong> that grants access to the
                MedCare National Board of Nursing and Vital Health Sciences
                Association benefits (digital member card, directory listing, CE
                tracker, member discount, members-only library, verifiable badge).
              </p>
              <p>
                You may cancel at any time via the Stripe Customer Portal — your
                access continues through the end of the current billing period and
                no further charges will be made. <strong>Past months are
                non-refundable</strong>, and there is no proration for partial
                months.
              </p>
            </>
          ),
        },
        {
          id: 'deferred-tuition',
          icon: AlertTriangle,
          title: '4. Deferred Stated Tuition (ETA-671 Apprenticeship Agreement)',
          body: (
            <>
              <p>
                Each program carries a "Stated Tuition" (e.g., $2,500 for CNA
                Training Program, $8,500 for PN). After the $99 program
                enrollment fee is paid, the remainder is recorded as a
                <strong> Deferred Tuition</strong> balance under the signed ETA-671
                Apprenticeship Agreement.
              </p>
              <p>
                The Deferred Tuition is <strong>payable only on material default</strong>
                of the agreement (for example, withdrawing without completing the
                program after issuance of a certificate, or breaching another
                contractual term). Refunds do not apply to the Deferred Tuition because
                no tuition payment is made by the student during the apprenticeship.
              </p>
            </>
          ),
        },
        {
          id: 'chargebacks',
          icon: XCircle,
          title: '5. Chargebacks &amp; Disputes',
          body: (
            <>
              <p>
                Any chargeback or dispute filed with a card issuer or bank in
                violation of this no-refund policy will be contested by MedCare
                with documentation of (a) the student&apos;s signed enrollment
                agreement, (b) the date of payment, (c) access logs showing course
                use, and (d) a copy of this Refund Policy.
              </p>
              <p>
                Students whose chargebacks are reversed in MedCare&apos;s favor
                will have their access reinstated and may be assessed an
                administrative processing fee. Repeat chargeback abuse may result
                in permanent suspension from all MedCare programs and association
                membership.
              </p>
            </>
          ),
        },
        {
          id: 'exceptions',
          icon: AlertTriangle,
          title: '6. Limited Exceptions',
          body: (
            <>
              <p>
                MedCare may, at its sole discretion, issue a goodwill credit (NOT a
                refund) in the following narrow situations:
              </p>
              <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.7 }}>
                <li>Duplicate charge caused by a documented technical error.</li>
                <li>A program is permanently discontinued before the student is able to access any module.</li>
                <li>Other extraordinary circumstances approved by the MedCare Director.</li>
              </ul>
              <p style={{ marginTop: 10 }}>
                Goodwill credits expire 12 months after issuance and are not
                transferable or redeemable for cash.
              </p>
            </>
          ),
        },
        {
          id: 'how-to-cancel',
          icon: RefreshCw,
          title: '7. How to Cancel an Association Membership',
          body: (
            <>
              <p>To cancel your $9.99 / month association membership:</p>
              <ol style={{ margin: '8px 0 0 20px', lineHeight: 1.7 }}>
                <li>Sign in to your MedCare account.</li>
                <li>Go to <Link to="/membership/dashboard" style={{ color: '#0d9488' }}>Member Dashboard</Link>.</li>
                <li>Click <strong>Manage Billing</strong> to open the Stripe Customer Portal.</li>
                <li>Click <strong>Cancel subscription</strong>. Access continues until the end of the current billing period.</li>
              </ol>
            </>
          ),
        },
        {
          id: 'contact',
          icon: Mail,
          title: '8. Contact &amp; Questions',
          body: (
            <>
              <p>
                Questions about this Refund Policy? Email
                <a href="mailto:medcare.nursing@yahoo.com" style={{ color: '#0d9488', marginLeft: 4 }}>medcare.nursing@yahoo.com</a>
                {' '}or call <strong>901.625.1201</strong>. Mail-in correspondence: MedCare Nursing
                Institute &amp; Training Academy, 3150 E. Shelby Dr, Memphis TN 38118.
              </p>
              <p>
                This Refund Policy is incorporated by reference into every signed
                ETA-671 Apprenticeship Agreement and every association membership
                subscription. Updates to this policy will be effective from the
                date posted at the bottom of this page.
              </p>
            </>
          ),
        },
      ].map((s) => {
        const Icon = s.icon;
        return (
          <section
            key={s.id}
            data-testid={`refund-section-${s.id}`}
            style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: 14,
              padding: 26,
              marginBottom: 16,
              boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
            }}
          >
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: '#f1f5f9', color: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={20} />
              </div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }} dangerouslySetInnerHTML={{ __html: s.title }} />
            </div>
            <div style={{ color: '#334155', lineHeight: 1.7, fontSize: '0.97rem' }}>{s.body}</div>
          </section>
        );
      })}

      <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem', marginTop: 28 }}>
        Last updated: February 2026 · MedCare Nursing Institute &amp; Training Academy
        · 3150 E. Shelby Dr, Memphis TN 38118 · 901.625.1201
      </div>
    </div>
  </div>
);

export default RefundPolicyPage;
